import {
  DEFAULT_THEME,
  type AgentResponse,
  type AgentTheme,
  type ConversationResponse,
  type DocumentResponse,
} from "@bubble/core";

export const STEP_ORDER = ["knowledge", "answers", "test", "look", "install"] as const;
export type StepId = "create" | (typeof STEP_ORDER)[number];

export type Progress = Record<StepId, boolean> & { reading: boolean };

const STEP_LABELS: Record<StepId, string> = {
  create: "Create your agent",
  knowledge: "Add your knowledge",
  answers: "Set how it answers",
  test: "Test it",
  look: "Customize the look",
  install: "Install on your site",
};

export function stepLabel(step: StepId): string {
  return STEP_LABELS[step];
}

export function progressFor(input: {
  agent: AgentResponse;
  documents: DocumentResponse[];
  conversations: ConversationResponse[];
  marked: { answers: boolean; look: boolean; test: boolean };
}): Progress {
  const published = Boolean(input.agent.publishedPolicyVersionId);
  return {
    create: true,
    knowledge: input.documents.some((document) => document.status === "ready"),
    answers: published || input.marked.answers,
    test: input.marked.test || input.conversations.some((conversation) => conversation.channel === "preview" || conversation.channel === "web"),
    look: input.marked.look || themeCustomized(input.agent.theme),
    install: published && input.agent.allowedDomains.length > 0,
    reading: input.documents.some((document) => document.status === "queued" || document.status === "processing"),
  };
}

export function nextStep(progress: Progress): Exclude<StepId, "create"> {
  return STEP_ORDER.find((step) => !progress[step]) ?? "test";
}

export function nextLabel(progress: Progress): string {
  const step = nextStep(progress);
  if (STEP_ORDER.every((item) => progress[item])) return "Open the chat";
  return stepLabel(step);
}

export function stepPath(agentId: string, step: StepId): string {
  if (step === "create") return "/app";
  return `/app/agents/${agentId}/${step}`;
}

export function themeCustomized(theme: AgentTheme): boolean {
  return (
    theme.color.toLowerCase() !== DEFAULT_THEME.color ||
    theme.greeting !== DEFAULT_THEME.greeting ||
    Boolean(theme.title) ||
    Boolean(theme.avatarUrl)
  );
}

export function statusLabel(status: DocumentResponse["status"]): string {
  if (status === "queued") return "Waiting";
  if (status === "processing") return "Reading";
  if (status === "ready") return "Ready";
  return "Needs attention";
}

export function previewNote(agent: AgentResponse, newerDraft: boolean): string {
  if (agent.isSample) return "Sample cafe. Answers come from Harbor & Rye's notes.";
  if (!agent.publishedPolicyVersionId) return "Only you can see this chat until you make it live.";
  if (newerDraft) return "This includes answers that are not on your site yet.";
  return "This matches the chat on your site.";
}

export const SAMPLE_QUESTIONS = ["What time do you open?", "How long does shipping take?", "Can I get a wholesale discount?"];
export const OWN_QUESTIONS = ["What can you help me with?", "What are your hours?"];

export function readMarks(agentId: string): { answers: boolean; look: boolean; test: boolean } {
  return { answers: marked(agentId, "answers"), look: marked(agentId, "look"), test: marked(agentId, "test") };
}

export function markStep(agentId: string, step: "answers" | "look" | "test"): void {
  try {
    localStorage.setItem(storageKey(agentId, step), "1");
  } catch {
    // Private browsing can block storage. A practice chat is still saved on the server.
  }
}

function marked(agentId: string, step: string): boolean {
  try {
    return localStorage.getItem(storageKey(agentId, step)) === "1";
  } catch {
    return false;
  }
}

function storageKey(agentId: string, step: string): string {
  return `bubble-setup:${agentId}:${step}`;
}
